import React from 'react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { ReadingMeta } from '../components/ReadingMeta';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import { SILIKA_COMPARISON } from '../data/science';
import { Info } from 'lucide-react';
import { SITE_URL, CONTACT } from '../data/site';
import AnswerBlock from '../components/geo/AnswerBlock';
import Breadcrumbs from '../components/geo/Breadcrumbs';
import AuthorMeta from '../components/geo/AuthorMeta';
import RelatedTopics from '../components/geo/RelatedTopics';
import FAQ from '../components/geo/FAQ';
import { FAQS, RELATED, PAGE_DATES } from '../data/geo';

const Silikastaub = () => {
  const { t, lang } = useLang();
  const cols = SILIKA_COMPARISON.columns[lang];
  const answer = lang === 'de'
    ? 'Metakaolin und Silikastaub sind beide hochreaktive puzzolanische Zusatzstoffe. Metakaolin ist ein amorphes Alumosilikat (SiO\u2082/Al\u2082O\u2083), Silikastaub \u00fcberwiegend amorphes SiO\u2082. Sie unterscheiden sich unter anderem in Frischbetonverhalten, Flie\u00dfmittelbedarf, Farbwirkung und typischen Anwendungen. Die Auswahl ist stets anwendungsbezogen im jeweiligen Bindemittelsystem zu bewerten.'
    : 'Metakaolin and silica fume are both highly reactive pozzolanic additions. Metakaolin is an amorphous aluminosilicate (SiO\u2082/Al\u2082O\u2083), silica fume predominantly amorphous SiO\u2082. They differ in fresh-concrete behaviour, superplasticiser demand, colour effect and typical applications. Selection must always be assessed on an application basis in the respective binder system.';
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'TechArticle',
    headline: t('silika.title'), description: answer, inLanguage: lang,
    mainEntityOfPage: `${SITE_URL}/wissen/metakaolin-silikastaub`,
    datePublished: PAGE_DATES.silika.published, dateModified: PAGE_DATES.silika.reviewed,
    author: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
    publisher: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
  };
  return (
    <div>
      <SEO title={t('silika.title')} description={answer} path="/wissen/metakaolin-silikastaub" lang={lang} keywords={SEO_KEYWORDS[lang]} jsonLd={jsonLd} />
      <PageHeader label={t('silika.label')} title={t('silika.title')} lead={t('silika.lead')} imageKey="powderMagnifier" />
      <section className="bg-white py-14 lg:py-20">
        <div className="container-wide">
          <Breadcrumbs items={[{ label: t('wissen.label'), to: '/wissen' }, { label: t('silika.title'), to: '/wissen/metakaolin-silikastaub' }]} />
          <div className="flex items-center gap-4 mt-6 mb-6">
            <ReadingMeta minutes={t('silika.readMinutes')} />
          </div>
          <div className="max-w-2xl"><AnswerBlock>{answer}</AnswerBlock></div>
          <h2 className="mt-10 text-[22px] font-extrabold tracking-tight text-slate-900">{t('silika.tableTitle')}</h2>
          <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-left border-collapse min-w-[680px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {cols.map((c, i) => (
                    <th key={i} className={`px-4 py-3 text-[12.5px] font-bold ${i === 0 ? 'uppercase tracking-wide text-slate-500' : 'text-slate-900'}`}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SILIKA_COMPARISON.rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                    <td className="px-4 py-3 text-[13.5px] font-semibold text-slate-700">{r.c[lang]}</td>
                    <td className="px-4 py-3 text-[13.5px] text-slate-700">{r.mk[lang]}</td>
                    <td className="px-4 py-3 text-[13.5px] text-slate-700">{r.sf[lang]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-5 py-4 max-w-4xl">
            <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
            <p className="text-[13.5px] text-slate-700 leading-relaxed">{t('silika.note')}</p>
          </div>
          <div className="mt-12 max-w-3xl"><FAQ items={FAQS.silika[lang]} /></div>
          <div className="mt-10 max-w-3xl"><AuthorMeta dates={PAGE_DATES.silika} /></div>
          <div className="mt-12"><RelatedTopics items={RELATED.silika} /></div>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default Silikastaub;
